import { Field, Form, Formik, FormikHelpers } from "formik";
import * as Yup from "yup";
import Button from "shared/components/Button";
import DataItem from "shared/components/DataItem";
import CalibrationApi from "./calibrationApi";
import { isNullorUndefined } from "shared/helpers/notNullNorUndefined";
import { RootState } from "app/store";
import { useAppDispatch } from "app/hooks";
import { setOpenPanel } from "features/settings/details-slice";

type FormFields = {
  orientationAngle: number;
  altitudeAboveField: number;
};

const calibrationSchema: Yup.ObjectSchema<FormFields> = Yup.object().shape({
  orientationAngle: Yup.number()
    .typeError("Invalid number format")
    .required("Required")
    .min(-180, "Values cannot be less than -180°")
    .max(180, "Values cannot be more than 180°"),
  altitudeAboveField: Yup.number()
    .typeError("Invalid number format")
    .required("Required")
    .min(0, "Take-off height cannot be less than 0m"),
});

interface CalibrateFormProps {
  initialOrientationAngle: number;
  initialDroneAltitude: number;
  //
  guid: RootState["panorama"]["guid"];
  currentFieldId:
    | NonNullable<RootState["panorama"]["currentFieldDetails"]>["fid"]
    | undefined;
  currentFieldName:
    | NonNullable<RootState["panorama"]["currentFieldDetails"]>["name"]
    | undefined;
  fieldPanoImageId:
    | NonNullable<
        RootState["panorama"]["currentFieldDetails"]
      >["field_pano_image_id"]
    | undefined;
  //
  loading: boolean;
  setLoading: React.Dispatch<
    React.SetStateAction<CalibrateFormProps["loading"]>
  >;
}

const CalibrateForm: React.FC<CalibrateFormProps> = ({
  initialOrientationAngle,
  initialDroneAltitude,
  guid,
  currentFieldId,
  currentFieldName,
  fieldPanoImageId,
  loading,
  setLoading,
}) => {
  const dispatch = useAppDispatch();
  const missingSupportingData =
    isNullorUndefined(currentFieldId) ||
    isNullorUndefined(guid) ||
    isNullorUndefined(fieldPanoImageId);

  const onSubmit = async (
    formikValues: FormFields,
    formikHelpers: FormikHelpers<FormFields>
  ) => {
    try {
      const errors = await formikHelpers.validateForm();
      if (Object.keys(errors).length > 0) {
        // NOTE: Shouldn't get here since validationSchema is provided
        console.warn("Validation errors found", errors);
        return;
      }
      if (missingSupportingData) {
        console.warn("Possible missing data, check if any of these are null", {
          currentFieldId,
          guid,
          fieldPanoImageId,
        });
        return;
      }
      setLoading(true);
      const result = await CalibrationApi.setCalibration({
        currentFieldID: currentFieldId,
        panoramaGuid: guid,
        panoImageId: fieldPanoImageId,
        orientationAngle: formikValues.orientationAngle,
        heightAboveTakeOff: formikValues.altitudeAboveField,
      });
      setLoading(false);
      if (result.success) {
        // Force a reload
        window.krpano.call(
          `skin_loadscene(${currentFieldName}, get(scene_load.blend), 0, true)`
        );
        dispatch(setOpenPanel(null));
      } else {
        // TODO: Handle error
        console.error("Calibration failed", result.errors);
      }
    } catch (error) {
      console.error("An error occurred during calibration", error);
    }
  };

  const inputUnitsClass = loading ? "MutedText" : "";

  return (
    <Formik<FormFields>
      initialValues={{
        orientationAngle: initialOrientationAngle,
        altitudeAboveField: initialDroneAltitude,
      }}
      validationSchema={calibrationSchema}
      onSubmit={onSubmit}
      validateOnChange
      enableReinitialize
    >
      {({ values, errors, isValid, touched }) => (
        <Form className="DisplayFlex FlexColumn" autoComplete="off">
          <DataItem
            left={<label htmlFor="orientationAngle">Orientation angle</label>}
            middle={
              <div className="DisplayFlex AlignCentre LargeGap">
                <Field
                  type="number"
                  className="InputField"
                  id="orientationAngle"
                  name="orientationAngle"
                  disabled={loading}
                />
                <span className={inputUnitsClass}>(degs)</span>
              </div>
            }
          />
          {errors.orientationAngle && touched.orientationAngle && (
            <span className="ErrorMessage WarningText">
              {errors.orientationAngle}
            </span>
          )}
          <DataItem
            left={
              <label htmlFor="altitudeAboveField">Height above take off</label>
            }
            middle={
              <div className="DisplayFlex AlignCentre LargeGap">
                <Field
                  type="number"
                  className="InputField"
                  id="altitudeAboveField"
                  name="altitudeAboveField"
                  step={0.1}
                  disabled={loading}
                />
                <span className={inputUnitsClass}>(m)</span>
              </div>
            }
          />
          {errors.altitudeAboveField && touched.altitudeAboveField && (
            <span className="ErrorMessage WarningText">
              {errors.altitudeAboveField}
            </span>
          )}
          {isNullorUndefined(currentFieldId) && (
            <span className="ErrorMessage WarningText">
              The field does not have a valid id
            </span>
          )}
          {isNullorUndefined(guid) && (
            <span className="ErrorMessage WarningText">
              The panorama does not have a valid GUID
            </span>
          )}
          {isNullorUndefined(fieldPanoImageId) && (
            <span className="ErrorMessage WarningText">
              The image does not have a valid id
            </span>
          )}
          <Button
            colour="Theme"
            additionalClassNames="Medium Wide"
            disabled={
              loading ||
              !isValid ||
              // Values not changed from initial values
              (values.altitudeAboveField === initialDroneAltitude &&
                values.orientationAngle === initialOrientationAngle) ||
              missingSupportingData
            }
            // NOTE: No need to include `onClick`
            // onClick={submitForm}
            // REASON: Formik handles form submission, provided `type="submit"`
            type="submit"
          >
            SUBMIT CHANGES
          </Button>
        </Form>
      )}
    </Formik>
  );
};

export default CalibrateForm;
